import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Tab, Tabs, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Accordion, AccordionSummary,
  AccordionDetails, CircularProgress, Alert, Stack, ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { excuseApi } from '../../commons/http';

const round2 = (v) => Math.round(v * 100) / 100;

const STATUS_COLORS = {
  CONFIRMING: 'warning',
  OPEN: 'info',
  PACKAGED: 'secondary',
  SHIPPED: 'primary',
  DELIVERED: 'success',
};

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

function ProductSummaryTable({ products }) {
  if (!products || products.length === 0) {
    return <Alert severity="info" sx={{ mb: 2 }}>Không có sản phẩm nào cần chuẩn bị.</Alert>;
  }

  return (
    <TableContainer component={Paper} sx={{ mb: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold' }}>Sản phẩm</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Tổng SL</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Số lần giao</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products.map((p) => (
            <TableRow key={p.product_id}>
              <TableCell>{p.product_name}</TableCell>
              <TableCell align="right">{p.total_quantity}</TableCell>
              <TableCell align="right">{p.delivery_count}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DeliveryList({ deliveries }) {
  if (!deliveries || deliveries.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
        Chi tiết giao hàng ({deliveries.length})
      </Typography>
      {deliveries.map((d, idx) => (
        <Accordion key={`${d.delivery_id}-${d.date}-${idx}`} defaultExpanded={deliveries.length <= 5}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
              <Typography sx={{ fontWeight: 'bold', minWidth: 100 }}>
                {d.date}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {d.weekday}
              </Typography>
              <Typography sx={{ flexGrow: 1 }}>
                {d.customer_name} — {d.menu_title}
              </Typography>
              {d.delivery_label && (
                <Chip label={d.delivery_label} size="small" variant="outlined" />
              )}
              {d.order && (
                <Chip
                  label={`${d.order.name} (${d.order.status})`}
                  size="small"
                  color={STATUS_COLORS[d.order.status] || 'default'}
                />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {d.customer_address && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                Địa chỉ: {d.customer_address}
              </Typography>
            )}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sản phẩm</TableCell>
                  <TableCell align="right">Số lượng</TableCell>
                  <TableCell>Đơn vị</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {d.items.map((item, iIdx) => (
                  <TableRow key={iIdx}>
                    <TableCell>{item.product_name}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell>{item.weight_text}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

function DailySummary({ dailySummary, deliveries }) {
  if (!dailySummary || dailySummary.length === 0) {
    return <Alert severity="info" sx={{ mb: 2 }}>Không có sản phẩm nào cần chuẩn bị.</Alert>;
  }

  // Pivot: rows = products, columns = days
  const productMap = {};
  for (const day of dailySummary) {
    for (const p of day.products) {
      if (!productMap[p.product_id]) {
        productMap[p.product_id] = { product_id: p.product_id, product_name: p.product_name, total: 0, days: {} };
      }
      productMap[p.product_id].days[day.date] = p.total_quantity;
      productMap[p.product_id].total += p.total_quantity;
    }
  }
  const products = Object.values(productMap).sort((a, b) => b.total - a.total);

  // Count orders per day
  const orderCountByDay = {};
  let totalOrders = 0;
  for (const d of (deliveries || [])) {
    if (!orderCountByDay[d.date]) orderCountByDay[d.date] = 0;
    orderCountByDay[d.date]++;
    totalOrders++;
  }

  return (
    <TableContainer component={Paper} sx={{ mb: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold', position: 'sticky', left: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
              Sản phẩm
            </TableCell>
            {dailySummary.map((day) => (
              <TableCell key={day.date} align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {day.weekday}<br />{day.date.slice(5)}
              </TableCell>
            ))}
            <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e9' }}>
              Tổng
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow sx={{ backgroundColor: '#fff3e0' }}>
            <TableCell sx={{ fontWeight: 'bold', position: 'sticky', left: 0, backgroundColor: '#fff3e0', zIndex: 1 }}>
              Số đơn hàng
            </TableCell>
            {dailySummary.map((day) => (
              <TableCell key={day.date} align="right" sx={{ fontWeight: 'bold' }}>
                {orderCountByDay[day.date] || 0}
              </TableCell>
            ))}
            <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e9' }}>
              {totalOrders}
            </TableCell>
          </TableRow>
          {products.map((p) => (
            <TableRow key={p.product_id}>
              <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1 }}>
                {p.product_name}
              </TableCell>
              {dailySummary.map((day) => (
                <TableCell key={day.date} align="right">
                  {p.days[day.date] || '-'}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e9' }}>
                {round2(p.total)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function PeriodContent({ data, showDaily }) {
  if (!data) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {showDaily ? 'Sản phẩm theo ngày' : 'Tổng hợp sản phẩm'}
      </Typography>
      {showDaily
        ? <DailySummary dailySummary={data.daily_summary} deliveries={data.deliveries} />
        : <ProductSummaryTable products={data.product_summary} />
      }
      <DeliveryList deliveries={data.deliveries} />
    </Box>
  );
}

const MenuReport = ({ setPageTitle }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [tab, setTab] = useState(0);
  const [viewMode, setViewMode] = useState('total');

  useEffect(() => {
    setPageTitle('Báo cáo chuẩn bị Menu');
  }, [setPageTitle]);

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await excuseApi({ path: '/menus/report', method: 'GET' });
        if (res.status === 200) {
          setReport(res.data.data);
        } else {
          setError('Không thể tải báo cáo');
        }
      } catch (err) {
        setError('Có lỗi xảy ra khi tải báo cáo');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  }

  const tabLabels = [
    `Ngày mai (${report?.tomorrow?.deliveries?.length || 0})`,
    `Tuần này (${report?.this_week?.deliveries?.length || 0})`,
    `Tuần sau (${report?.next_week?.deliveries?.length || 0})`,
    `Tháng này (${report?.this_month?.deliveries?.length || 0})`,
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Paper sx={{ flexGrow: 1 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            {tabLabels.map((label, i) => (
              <Tab key={i} label={label} />
            ))}
          </Tabs>
        </Paper>
        {tab > 0 && (
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => { if (v) setViewMode(v); }}
            size="small"
          >
            <ToggleButton value="total">Tổng hợp</ToggleButton>
            <ToggleButton value="daily">Theo ngày</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      <TabPanel value={tab} index={0}>
        <PeriodContent data={report?.tomorrow} showDaily={false} />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <PeriodContent data={report?.this_week} showDaily={viewMode === 'daily'} />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <PeriodContent data={report?.next_week} showDaily={viewMode === 'daily'} />
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <PeriodContent data={report?.this_month} showDaily={viewMode === 'daily'} />
      </TabPanel>
    </Box>
  );
};

export default MenuReport;
